import translations from "../components/common/Translations";

const INVALID_CREDENTIAL_PATTERNS = [
  "invalid credentials",
  "email or password",
  "wrong password",
  "incorrect password",
  "bad credentials",
  "authentication failed",
];

const isInvalidCredentialsMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return INVALID_CREDENTIAL_PATTERNS.some((pattern) => normalized.includes(pattern));
};

const isNetworkError = (error) => {
  if (!error?.response) {
    return true;
  }
  const code = String(error?.code || "");
  return code === "ERR_NETWORK" || code === "ECONNABORTED";
};

const getTranslations = (langMode) => translations[langMode] || translations.en;

const getApiMessage = (envelope) => {
  if (!envelope || typeof envelope !== "object") {
    return "";
  }
  const bodyError = envelope?.body?.error;
  const bodyValue = envelope?.body?.value;
  if (typeof bodyError === "string" && bodyError.trim()) {
    return bodyError;
  }
  if (typeof bodyValue === "string" && bodyValue.trim()) {
    return bodyValue;
  }
  return "";
};

/**
 * Maps login API / transport failures to user-facing messages (i18n via langMode).
 */
export const getLoginErrorMessage = (error, langMode = "en") => {
  const t = getTranslations(langMode);
  const envelope = error?.envelope || error?.response?.data;
  const httpStatus = error?.httpStatus ?? error?.response?.status;
  const responseCode = error?.responseCode ?? envelope?.header?.code;
  const apiMessage = getApiMessage(envelope);

  if (responseCode === 612 || isInvalidCredentialsMessage(apiMessage)) {
    return t.emailidorpass;
  }

  if (httpStatus === 401 || responseCode === 601) {
    if (isInvalidCredentialsMessage(apiMessage)) {
      return t.emailidorpass;
    }
    if (apiMessage) {
      return apiMessage;
    }
    return t.emailidorpass;
  }

  if (responseCode === 608 || httpStatus === 403) {
    return t.accessdenied;
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (isNetworkError(error) || /network error/i.test(String(error?.message || ""))) {
    return t.loginConnectionError;
  }

  if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
    return t.loginConnectionError;
  }

  const rawBody = error?.response?.data;
  if (rawBody && typeof rawBody === "object" && rawBody.error && !rawBody.header) {
    return t.loginConnectionError;
  }

  return t.loginFailed || t.loginConnectionError;
};

/**
 * Maps a login API envelope (success or error body) to a user-facing message, or null when no error.
 */
export const getLoginResponseErrorMessage = (responseData, langMode = "en") => {
  const t = getTranslations(langMode);
  const code = responseData?.header?.code;
  if (code === 600) {
    return null;
  }
  if (code === 612 || code === 613) {
    return code === 612 ? t.emailidorpass : null;
  }
  if (code === 608) {
    return t.accessdenied;
  }
  if (code === 601) {
    const message = String(responseData?.body?.value || "");
    if (isInvalidCredentialsMessage(message)) {
      return t.emailidorpass;
    }
    return message || t.loginFailed || t.loginConnectionError;
  }

  const bodyValue = responseData?.body?.value;
  if (typeof bodyValue === "string" && bodyValue.trim()) {
    return bodyValue;
  }
  return t.loginFailed || t.loginConnectionError;
};
